<template>
    <v-dialog
            v-model="showDialog"
            fullscreen
            hide-overlay
            transition="dialog-bottom-transition"
            persistent
    >
        <v-card flat height="100vh">
            <v-layout column fill-height>
                <v-flex shrink class="primary">
                    <!--Card Title-->
                    <v-card-title class="py-2 pr-2">
                        <img src="../../../public/import.svg" width="42px" class="pr-2"/>
                        <span class="title font-weight-light white--text">Import Manager</span>
                        <v-spacer></v-spacer>
                        <v-btn flat color="white" class="pa-0 ma-0 mr-1" @click="toggleHistory">
                            <v-icon left>{{ showHistory ? 'arrow_back' : 'history' }}</v-icon>
                            {{ showHistory ? 'Back To Import' : 'History' }}
                        </v-btn>
                        <v-btn flat icon color="white" class="pa-0 ma-0" @click="$emit('dismiss', false)">
                            <v-icon>close</v-icon>
                        </v-btn>
                    </v-card-title>
                </v-flex>

                <v-flex v-if="loadingHeader" class="text-xs-center pa-2">
                    <v-progress-circular
                            :size="40"
                            :width="5"
                            color="primary"
                            indeterminate
                    ></v-progress-circular>
                </v-flex>

                <v-flex v-else xs12 style="overflow-y: auto" class="scroll pa-0">

                    <run-history
                            v-if="showHistory"
                            :runs="runs"
                            :loading="loadingRuns"
                            @refresh="loadRuns"
                            @select="resumeRun($event)"
                    />

                    <v-stepper v-else v-model="step" class="elevation-0">
                        <v-stepper-header class="elevation-0">
                            <v-stepper-step :complete="step > 1" :step="1">Upload</v-stepper-step>
                            <v-divider></v-divider>
                            <v-stepper-step :complete="step > 2" :step="2">Map</v-stepper-step>
                            <v-divider></v-divider>
                            <v-stepper-step :complete="step > 3" :step="3">Review</v-stepper-step>
                            <v-divider></v-divider>
                            <v-stepper-step :complete="step > 4" :step="4">Run</v-stepper-step>
                            <v-divider></v-divider>
                            <v-stepper-step :step="5">Report</v-stepper-step>
                        </v-stepper-header>
                        <v-stepper-items>
                            <v-stepper-content :step="1" class="pa-0">
                                <step-upload
                                        :templates="headerData.templates"
                                        :run="run"
                                        :busy="busy"
                                        @create-run="createRun($event)"
                                />
                            </v-stepper-content>
                            <v-stepper-content :step="2" class="pa-0">
                                <step-map
                                        v-if="run != null"
                                        :run="run"
                                        :template="selectedTemplate"
                                        :field-sets="headerData.fieldSets"
                                        :folder-groups="headerData.folderGroups"
                                        :busy="busy"
                                        @back="step = 1"
                                        @save-mapping="saveMapping($event)"
                                />
                            </v-stepper-content>
                            <v-stepper-content :step="3" class="pa-0">
                                <step-review
                                        v-if="preview != null"
                                        :preview="preview"
                                        :busy="busy"
                                        @back="step = 2"
                                        @bulk-action="applyBulkAction($event)"
                                        @folder-action="applyFolderAction($event)"
                                        @execute="executeRun"
                                />
                            </v-stepper-content>
                            <v-stepper-content :step="4" class="pa-0">
                                <step-run
                                        v-if="run != null"
                                        :run="run"
                                        :counts="counts"
                                        :cancelling="cancelling"
                                        @cancel-run="cancelRun"
                                />
                            </v-stepper-content>
                            <v-stepper-content :step="5" class="pa-0">
                                <step-report
                                        v-if="run != null"
                                        :run="run"
                                        :report="report"
                                        :counts="counts"
                                        :loading-report="loadingReport"
                                        :downloading="downloadingReport"
                                        @download="downloadWorkbook"
                                        @restart="resetWizard"
                                />
                            </v-stepper-content>
                        </v-stepper-items>
                    </v-stepper>
                </v-flex>
            </v-layout>
        </v-card>
    </v-dialog>
</template>

<script>

    import {codeBaseMixin} from "../../codeBaseMixin";
    import StepUpload from "./StepUpload";
    import StepMap from "./StepMap";
    import StepReview from "./StepReview";
    import StepRun from "./StepRun";
    import StepReport from "./StepReport";
    import RunHistory from "./RunHistory";

    export default {
        mixins: [codeBaseMixin],
        components: {
            StepUpload,
            StepMap,
            StepReview,
            StepRun,
            StepReport,
            RunHistory
        },
        props: {
            showDialog: {
                type: Boolean,
                default: false
            },
            userInfo: Object
        },
        data() {
            return {
                step: 1,
                loadingHeader: false,
                headerData: {
                    templates: [],
                    folderGroups: [],
                    fieldSets: []
                },
                headerLoaded: false,
                run: null,
                counts: null,
                preview: null,
                report: null,
                busy: false,
                cancelling: false,
                loadingReport: false,
                downloadingReport: false,
                showHistory: false,
                runs: [],
                loadingRuns: false,
                pollTimer: null,
                pollBusy: false
            }
        },
        watch: {
            showDialog: {
                handler(shown) {
                    if (shown && !this.headerLoaded && !this.loadingHeader) {
                        this.loadHeader();
                    }
                    if (!shown) {
                        this.stopPolling();
                    }
                },
                immediate: true
            }
        },
        beforeDestroy() {
            this.stopPolling();
        },
        computed: {
            baseUrl() {
                return '/import/v2/' + this.userInfo.entityID;
            },
            runUrl() {
                return this.run != null ? this.baseUrl + '/runs/' + this.run.id : null;
            },
            selectedTemplate() {
                if (this.run == null || this.run.fk_template == null) {
                    return null;
                }
                let matches = this.headerData.templates.filter(obj => obj.id == this.run.fk_template);
                return matches.length > 0 ? matches[0] : null;
            }
        },
        methods: {
            loadHeader() {
                this.loadingHeader = true;
                this.fetch({method: 'GET', url: this.baseUrl + '/header'})
                    .then((data) => {
                        this.headerData = {
                            templates: data.templates || [],
                            folderGroups: data.folderGroups || [],
                            fieldSets: data.fieldSets || []
                        };
                        this.headerLoaded = true;
                        this.loadingHeader = false;
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.loadingHeader = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            createRun(payload) {
                this.busy = true;
                this.fetch({method: 'POST', url: this.baseUrl + '/runs', body: payload})
                    .then((data) => {
                        this.run = data.run;
                        this.counts = null;
                        this.preview = null;
                        this.report = null;
                        this.busy = false;
                        this.step = 2;
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.busy = false;
                        this.showSnack('Error', typeof e == 'string' && e != '' ? e : 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            saveMapping(payload) {
                this.busy = true;
                this.fetch({method: 'PATCH', url: this.runUrl, body: payload})
                    .then((data) => {
                        this.run = data.run;
                        return this.fetch({method: 'POST', url: this.runUrl + '/preview'});
                    })
                    .then((data) => {
                        this.preview = data;
                        this.busy = false;
                        this.step = 3;
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.busy = false;
                        this.showSnack('Error', typeof e == 'string' && e != '' ? e : 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            applyBulkAction(payload) {
                this.busy = true;
                this.fetch({
                    method: 'PATCH',
                    url: this.runUrl + '/folders',
                    body: {match_state: payload.matchState, action: payload.action}
                })
                    .then((data) => {
                        this.preview = Object.assign({}, this.preview, data);
                        this.busy = false;
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.busy = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            applyFolderAction(payload) {
                let body = {action: payload.action};
                if (payload.fkMatchedFolder != null) {
                    body.fk_matched_folder = payload.fkMatchedFolder;
                }
                this.busy = true;
                this.fetch({
                    method: 'PATCH',
                    url: this.runUrl + '/folders/' + payload.folderId,
                    body: body
                })
                    .then((data) => {
                        this.preview = Object.assign({}, this.preview, data);
                        this.busy = false;
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.busy = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            executeRun() {
                this.busy = true;
                this.fetch({method: 'POST', url: this.runUrl + '/execute'})
                    .then((data) => {
                        this.run = data.run;
                        this.busy = false;
                        this.step = 4;
                        this.startPolling();
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.busy = false;
                        this.showSnack('Error', typeof e == 'string' && e != '' ? e : 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            cancelRun() {
                this.cancelling = true;
                this.fetch({method: 'POST', url: this.runUrl + '/cancel'})
                    .then((data) => {
                        this.run = data.run;
                        this.cancelling = false;
                        if (this.run.status != 'running') {
                            this.stopPolling();
                            if (this.step == 4) {
                                this.openReport();
                            }
                        }
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.cancelling = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            startPolling() {
                this.stopPolling();
                this.pollTimer = setInterval(() => {
                    this.refreshRun();
                }, 2000);
            },

            stopPolling() {
                if (this.pollTimer != null) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                }
            },

            refreshRun() {
                if (this.run == null || this.pollBusy) {
                    return;
                }
                this.pollBusy = true;
                this.fetch({method: 'GET', url: this.runUrl})
                    .then((data) => {
                        this.run = data.run;
                        this.counts = data.counts;
                        this.pollBusy = false;
                        if (this.run.status != 'running') {
                            this.stopPolling();
                            if (this.step == 4) {
                                this.openReport();
                            }
                        }
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.pollBusy = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            openReport() {
                this.loadingReport = true;
                this.report = null;
                this.step = 5;
                this.fetch({method: 'GET', url: this.runUrl + '/report'})
                    .then((data) => {
                        this.report = data;
                        this.loadingReport = false;
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.loadingReport = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            downloadWorkbook() {
                this.downloadingReport = true;
                this.fetch({method: 'GET', url: this.runUrl + '/report?format=xlsx'})
                    .then((data) => {
                        this.downloadingReport = false;
                        try {
                            let byteChars = atob(data.contentB64);
                            let bytes = new Uint8Array(byteChars.length);
                            for (let i = 0; i < byteChars.length; i++) {
                                bytes[i] = byteChars.charCodeAt(i);
                            }
                            let blob = new Blob([bytes], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
                            let url = URL.createObjectURL(blob);
                            let a = document.createElement('a');
                            a.href = url;
                            a.download = data.filename || 'import-result.xlsx';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        } catch (e) {
                            // eslint-disable-next-line
                            console.log(e);
                            this.showSnack('Error', 'The workbook could not be downloaded. Please try again.', 'Close', 'red');
                        }
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.downloadingReport = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            loadRuns() {
                this.loadingRuns = true;
                this.fetch({method: 'GET', url: this.baseUrl + '/runs'})
                    .then((data) => {
                        this.runs = data.runs || [];
                        this.loadingRuns = false;
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.loadingRuns = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            toggleHistory() {
                this.showHistory = !this.showHistory;
                if (this.showHistory) {
                    this.loadRuns();
                }
            },

            resumeRun(selectedRun) {
                this.busy = true;
                this.fetch({method: 'GET', url: this.baseUrl + '/runs/' + selectedRun.id})
                    .then((data) => {
                        this.stopPolling();
                        this.run = data.run;
                        this.counts = data.counts;
                        this.preview = null;
                        this.report = null;
                        this.showHistory = false;
                        if (this.run.status == 'draft') {
                            this.busy = false;
                            this.step = 2;
                        } else if (this.run.status == 'previewed') {
                            // GET keeps the folder decisions already made — re-running
                            // the preview would rebuild the folder list and reset them
                            this.fetch({method: 'GET', url: this.runUrl + '/folders'})
                                .then((previewData) => {
                                    this.preview = previewData;
                                    this.busy = false;
                                    this.step = 3;
                                })
                                .catch((e) => {
                                    // eslint-disable-next-line
                                    console.log(e);
                                    this.busy = false;
                                    this.step = 2;
                                    this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                                });
                        } else if (this.run.status == 'running') {
                            this.busy = false;
                            this.step = 4;
                            this.startPolling();
                        } else {
                            this.busy = false;
                            this.openReport();
                        }
                    })
                    .catch((e) => {
                        // eslint-disable-next-line
                        console.log(e);
                        this.busy = false;
                        this.showSnack('Error', 'Network error. Please try again.', 'Close', 'red');
                    })
            },

            resetWizard() {
                this.stopPolling();
                this.run = null;
                this.counts = null;
                this.preview = null;
                this.report = null;
                this.busy = false;
                this.cancelling = false;
                this.step = 1;
            }
        }
    }
</script>

<style scoped>

</style>
